import type { LearnTopic } from "../topic-types";

/**
 * Grief & Trauma.
 *
 * Add new guides, exercises, prompts or worksheets by appending to a
 * section's `resources` array — no page or route changes required.
 */
export const GRIEF_TRAUMA_TOPIC: LearnTopic = {
  slug: "grief-trauma",
  emoji: "🕯️",
  title: "Grief & Trauma",
  tagline: "Loss that does not follow a schedule",
  theme: "lavender",
  category: "grief-loss",
  seoDescription:
    "Understand how grief and trauma intersect, why grief doesn't follow fixed stages, and how to make space for loss without a timeline. Trauma-informed guides, exercises and worksheets from Recovery Trauma™.",
  cardBlurb: "Grief doesn't follow neat stages — gentle support for loss of all kinds.",
  isNew: true,
  intro: [
    "Grief and trauma are often talked about separately, but for many people they arrive together. Loss can be traumatic, and trauma almost always involves loss — of safety, of time, of relationships, of the life or childhood someone might otherwise have had.",
    "You may be grieving a death, but you may also be grieving something less visible: an estranged relationship, years that felt unliveable, an identity you had to leave behind, or a childhood you never actually got. All of these are real losses, and all of them deserve space.",
    "This module does not offer a fixed process to work through. Grief is not a checklist, and there is no correct order or timeline. It moves in waves, it resurfaces without warning, and it can sit alongside relief, anger, guilt and love all at once.",
  ],
  closing:
    "Grief has no deadline and no required shape. If grief feels frightening, if it comes with thoughts of not wanting to be here, or if it has stopped shifting at all over a long period, please reach out — a GP, bereavement service, or trauma-informed counsellor can help, and our Crisis Resources page is always here.",
  sections: [
    {
      slug: "understand",
      emoji: "📚",
      title: "Understanding grief and trauma",
      blurb: "How grief and trauma overlap, and why loss takes many forms.",
      intro:
        "Plain-English education first. There is nothing to do yet except read, at whatever pace feels right.",
      resources: [
        {
          slug: "trauma-and-grief",
          kind: "learn",
          title: "How trauma and grief intersect",
          summary: "Why traumatic experiences often carry grief within them.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "Trauma often involves loss, even when no one has died. It can mean the loss of safety, of trust, of a relationship as it used to be, of time that can't be lived again, or of a version of yourself that existed before something happened.",
            },
            {
              type: "list",
              title: "Ways grief and trauma can overlap",
              items: [
                "Grieving a person while also carrying traumatic memories connected to their death",
                "Grieving safety or trust after an experience of harm",
                "Grieving the relationship you wish you'd had with someone who is still alive",
                "Grieving time lost to illness, addiction, caregiving or survival",
                "Grieving a childhood, or version of yourself, that circumstances didn't allow",
              ],
            },
            {
              type: "note",
              text: "If a loss also involved frightening or overwhelming events, grief can be complicated by trauma responses like hypervigilance, flashbacks or numbness. This is common, and it doesn't mean you are grieving “wrong”.",
            },
          ],
        },
        {
          slug: "kinds-of-loss",
          kind: "learn",
          title: "Different kinds of loss",
          summary: "Grief is not only about death — many losses deserve to be named.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "Society often only recognises grief after a death, but many other losses are just as real, even when they come without a funeral, a ritual, or anyone else's acknowledgement. This is sometimes called disenfranchised grief.",
            },
            {
              type: "list",
              title: "Losses worth recognising",
              items: [
                "The death of someone close to you",
                "An estranged relationship — a parent, sibling, child or friend who is alive but no longer part of your life",
                "Years lost to trauma, illness, addiction or caregiving",
                "The loss of an identity, role, career or dream",
                "The childhood you needed but didn't get, or the parent you needed them to be",
                "The loss of a community, culture, home or sense of belonging",
              ],
            },
            {
              type: "prose",
              text: "You do not need anyone else's permission for a loss to count. If it feels like grief to you, it is grief.",
            },
          ],
        },
        {
          slug: "grief-is-not-stages",
          kind: "learn",
          title: "Grief is not a fixed set of stages",
          summary: "Why the “five stages” model is often misunderstood and misapplied.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "Many people have heard of the “five stages of grief” — denial, anger, bargaining, depression and acceptance — and assume grief should move through them in order, ending in a tidy resolution. This idea was originally developed to describe experiences of people facing their own terminal diagnosis, not a universal roadmap for all grief, and it has often been applied far beyond what it was meant to capture.",
            },
            {
              type: "note",
              text: "There is no required sequence, no fixed number of stages, and no timeline you should be hitting. Grief that doesn't look like the “stages” model is not grief done wrong.",
            },
            {
              type: "list",
              title: "What grief is more often like",
              items: [
                "Non-linear — feelings can return after long gaps, sometimes years later",
                "Layered — several emotions can be present at the same time",
                "Different every time — grief for one loss may look nothing like grief for another",
                "Ongoing in some form — many people describe learning to carry a loss, rather than “getting over” it",
                "Shaped by culture, relationship and circumstance, not just personal psychology",
              ],
            },
            {
              type: "prose",
              text: "If a stages model has ever made you feel like you were grieving incorrectly, that pressure was never necessary — it can be set down.",
            },
          ],
        },
        {
          slug: "common-grief-experiences",
          kind: "learn",
          title: "Common experiences within grief",
          summary: "Anniversary reactions, triggers, anger, guilt and numbness.",
          minutes: 5,
          blocks: [
            {
              type: "list",
              title: "Experiences many grieving people recognise",
              items: [
                "Anniversary reactions — feeling low, on edge or unsettled around dates connected to the loss, sometimes before you consciously realise why",
                "Grief triggers — a smell, song, place or phrase that brings grief rushing back, seemingly out of nowhere",
                "Anger — at the person who died or left, at circumstances, at other people, or at yourself",
                "Guilt — about things said or unsaid, done or undone, or simply about still being here",
                "Numbness — feeling strangely little, or disconnected from the loss, especially early on or during traumatic grief",
              ],
            },
            {
              type: "prose",
              text: "All of these are ordinary parts of grief, even the ones that feel uncomfortable or “wrong” to admit. Grief can hold contradictions — love and relief, sorrow and anger, missing someone and needing distance from them, all at once.",
            },
          ],
        },
      ],
    },
    {
      slug: "recognise",
      emoji: "🔎",
      title: "Recognising your grief",
      blurb: "Noticing how grief shows up for you, in your own time and your own way.",
      resources: [
        {
          slug: "how-grief-shows-up-for-you",
          kind: "reflect",
          title: "How does grief show up for you?",
          summary: "Gentle noticing, without needing to fit any particular model.",
          minutes: 6,
          blocks: [
            {
              type: "prompts",
              items: [
                "What losses am I carrying, named or unnamed?",
                "Has anyone else acknowledged this loss, or has it felt invisible to others?",
                "What does grief feel like in my body?",
                "Are there dates, places or reminders that tend to bring it forward?",
                "What feelings tend to arrive alongside the sadness — anger, guilt, relief, numbness, something else?",
              ],
            },
          ],
        },
        {
          slug: "anniversary-reactions",
          kind: "learn",
          title: "Understanding anniversary reactions",
          summary: "Why certain dates can stir up grief even without a clear reminder.",
          minutes: 3,
          blocks: [
            {
              type: "prose",
              text: "Anniversary reactions can include birthdays, the date of a death or loss, seasons, holidays, or even less obvious dates the body seems to remember before the mind does. Feeling unsettled around these times is common and doesn't mean anything has gone wrong.",
            },
          ],
        },
      ],
    },
    {
      slug: "practise",
      emoji: "🌿",
      title: "Practices for living alongside grief",
      blurb: "Ways to make space for grief without needing to resolve it on a schedule.",
      intro:
        "These are optional and can be paced however suits you. There's no requirement to feel ready, and stopping partway through is completely fine.",
      resources: [
        {
          slug: "planning-for-anniversaries",
          kind: "practice",
          title: "Planning ahead for anniversaries and hard dates",
          summary: "A gentle way to prepare for dates that tend to bring grief forward.",
          minutes: 8,
          blocks: [
            {
              type: "prose",
              text: "You don't need to avoid or “push through” hard dates. Planning ahead — even loosely — can make them feel more manageable, without pretending they aren't difficult.",
            },
            {
              type: "steps",
              title: "Planning for a difficult date",
              items: [
                { label: "Name the date and what it means", note: "Be specific — what does this date mark for you?" },
                { label: "Decide how much you want to mark it", note: "Quietly alone, with others, with a ritual, or not at all — all are valid." },
                { label: "Lower other demands on that day if possible", note: "Fewer obligations, more flexibility." },
                { label: "Plan one comforting thing", note: "Something familiar, soothing or meaningful, even if small." },
                { label: "Arrange support", note: "Let someone know the date matters, in case you want company or a check-in." },
                { label: "Have an aftercare plan", note: "Grief can land unexpectedly the next day too — be gentle with yourself either way." },
              ],
            },
          ],
        },
        {
          slug: "continuing-bonds",
          kind: "reflect",
          title: "Continuing bonds: remembering rather than “letting go”",
          summary: "A reflection on staying connected to what or who you've lost.",
          minutes: 8,
          blocks: [
            {
              type: "prose",
              text: "Grief is sometimes framed as a process of “letting go”, but many people find more comfort in continuing bonds — ongoing ways of staying connected to someone or something that has been lost, rather than severing the connection entirely.",
            },
            {
              type: "prompts",
              title: "Reflection prompts",
              items: [
                "What would I like to remember, rather than let go of?",
                "Is there a way I could honour this person, relationship, or part of my life, even in a small way?",
                "What did they, or it, teach me that I still carry?",
                "Is there a ritual, object, place or word that helps me feel connected?",
                "What might I want to say, if I could say anything?",
              ],
            },
            {
              type: "note",
              text: "Continuing bonds can include talking to someone who has died, keeping meaningful objects, marking dates in your own way, or simply allowing memories to stay present rather than pushing them away.",
            },
          ],
        },
        {
          slug: "grief-worksheet",
          kind: "worksheet",
          title: "Grief worksheet",
          summary: "A printable page for naming and holding a loss, at your own pace.",
          minutes: 10,
          blocks: [
            {
              type: "prose",
              text: "There is no need to complete this in one sitting, or at all. Use whatever parts feel useful.",
            },
          ],
          worksheetFields: [
            { label: "What I have lost", hint: "This can be a person, relationship, time period, identity, or anything else.", lines: 3 },
            { label: "Has this loss been acknowledged by others, or has it mostly gone unseen?", lines: 2 },
            { label: "Feelings I'm noticing right now (all of them, even contradictory ones)", lines: 3 },
            { label: "Dates or reminders that tend to bring this grief forward", lines: 2 },
            { label: "What I would like to remember or carry forward", lines: 3 },
            { label: "One way I could be gentle with myself this week", lines: 2 },
          ],
        },
      ],
    },
    {
      slug: "reflect",
      emoji: "✍️",
      title: "Reflect",
      blurb: "Longer-term journal prompts for exploring grief without a deadline.",
      resources: [
        {
          slug: "grief-over-time",
          kind: "reflect",
          title: "Grief over time",
          summary: "Prompts for noticing how grief shifts, without needing it to resolve.",
          minutes: 8,
          blocks: [
            {
              type: "prompts",
              items: [
                "How has this grief changed since it began — not disappeared, but changed?",
                "What has helped, even a little, on harder days?",
                "What do I wish other people understood about this loss?",
                "Is there a version of the “stages” idea that I've felt pressured by, and can I set that pressure down?",
                "What would carrying this loss gently, rather than fighting it, look like for me?",
              ],
            },
            { type: "quote", text: "Grief is not a problem to solve — it is love with nowhere familiar to go." },
          ],
        },
      ],
    },
  ],
  continueTo: [
    { label: "Emotional Flashbacks", to: "/learn/emotional-flashbacks" },
    { label: "Shame-Based Trauma", to: "/learn/shame-based-trauma" },
    { label: "Journal", to: "/journal" },
    { label: "Crisis Resources", to: "/crisis" },
  ],
};
